import * as THREE from 'three';
import type { RheinData } from '../data/types';

/**
 * Draws the Rhein as a schematic ribbon (flat water surface) from the real OSM
 * waterway centerline. The ribbon width comes from the OSM `width` tag where
 * available and is otherwise a documented approximation — not a surveyed bankline.
 */
export class WaterLayer {
  readonly group = new THREE.Group();
  private mesh: THREE.Mesh | null = null;
  private readonly y = -0.4;

  constructor() {
    this.group.name = 'water';
  }

  build(data: RheinData) {
    const half = (data.widthM || 300) / 2;
    const positions: number[] = [];
    const indices: number[] = [];

    for (const seg of data.segments) {
      const pts = seg.coords;
      if (pts.length < 2) continue;
      const base = positions.length / 3;
      for (let i = 0; i < pts.length; i++) {
        const [x, z] = pts[i];
        const prev = pts[Math.max(0, i - 1)];
        const next = pts[Math.min(pts.length - 1, i + 1)];
        let dx = next[0] - prev[0];
        let dz = next[1] - prev[1];
        const len = Math.hypot(dx, dz) || 1;
        dx /= len;
        dz /= len;
        const nx = -dz;
        const nz = dx;
        positions.push(x + nx * half, this.y, z + nz * half);
        positions.push(x - nx * half, this.y, z - nz * half);
      }
      for (let i = 0; i < pts.length - 1; i++) {
        const a = base + i * 2;
        const b = a + 1;
        const c = a + 2;
        const d = a + 3;
        indices.push(a, b, c, b, d, c);
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setIndex(indices);
    geo.computeVertexNormals();

    const mat = new THREE.MeshBasicMaterial({
      color: 0x1f4e73,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.renderOrder = -1;
    this.group.add(this.mesh);
  }

  setVisible(v: boolean) {
    this.group.visible = v;
  }

  get available(): boolean {
    return this.mesh !== null;
  }
}
